import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rewardApi } from '../../api/reward.api';
import { RewardConfigItem, RewardConfigRequest, RewardConfigHistoryItem, RewardType } from '../../types/Reward.api.type';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Plus, Pencil, Trash2, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, X } from 'lucide-react';
import toast from 'react-hot-toast';

/** 리워드 종류 한국어 매핑 */
const REWARD_TYPE_LABELS: Record<string, string> = {
  VOTE: '투표',
  VOTE_AD_BONUS: '투표광고보너스',
  DAILY_BONUS: '일일 보너스',
  DAILY_AD_BONUS: '오늘의투표광고보너스',
  WEEKLY_MILESTONE_1: '주간 1단계',
  WEEKLY_MILESTONE_2: '주간 2단계',
  WEEKLY_MILESTONE_3: '주간 3단계',
  MILESTONE_AD_BONUS: '누적투표보너스',
  CREATOR_MILESTONE: '설문 작성 보너스',
  FIRST_VOTE_BONUS: '첫 투표 보너스',
  ATTENDANCE: '출석',
  ATTENDANCE_AD_BONUS: '출석광고보너스',
  ATTENDANCE_STREAK: '연속출석보너스',
  ATTENDANCE_STREAK_SAVE: '연속출석유지보너스',
  COMMENT: '댓글 보상',
  COMMENT_AD_BONUS: '댓글 광고보너스',
  SURVEY_LIKE: '설문 좋아요 보상',
  SURVEY_LIKE_AD_BONUS: '설문 좋아요 광고보너스',
  COMMENT_LIKE: '댓글 좋아요 보상',
  COMMENT_LIKE_AD_BONUS: '댓글 좋아요 광고보너스',
  SIGNUP_BONUS: '가입 축하',
  WEEKLY_MISSION: '이번주 미션 완료',
  WEEKLY_MISSION_AD_BONUS: '이번주 미션 광고보너스',
};

/** 탭 카테고리 정의 */
const CATEGORY_TABS = ['투표', '마일스톤', '출석', '활동'] as const;
type CategoryTab = typeof CATEGORY_TABS[number];

/** 🔴 어드민에서 ON/OFF 토글이 필요한 프로모션 리워드.
 *  여기 빠지면 운영 중 켜고 끌 방법이 운영 DB 직접 UPDATE 뿐이 된다(M-024 에서 실제로 겪음). */
const TOGGLEABLE_TYPES: RewardType[] = ['FIRST_VOTE_BONUS', 'FIRST_ATTENDANCE_BONUS'];

const CATEGORY_TYPES: Record<CategoryTab, RewardType[]> = {
  '투표': ['VOTE', 'VOTE_AD_BONUS', 'DAILY_BONUS', 'DAILY_AD_BONUS', 'FIRST_VOTE_BONUS'],
  '마일스톤': ['WEEKLY_MILESTONE_1', 'WEEKLY_MILESTONE_2', 'WEEKLY_MILESTONE_3', 'MILESTONE_AD_BONUS'],
  '출석': ['FIRST_ATTENDANCE_BONUS', 'ATTENDANCE', 'ATTENDANCE_AD_BONUS', 'ATTENDANCE_STREAK', 'ATTENDANCE_STREAK_SAVE'],
  '활동': ['COMMENT', 'COMMENT_AD_BONUS', 'SURVEY_LIKE', 'SURVEY_LIKE_AD_BONUS', 'COMMENT_LIKE', 'COMMENT_LIKE_AD_BONUS', 'SIGNUP_BONUS', 'WEEKLY_MISSION', 'WEEKLY_MISSION_AD_BONUS'],
};

/** 초기 폼 상태 */
const initialForm: RewardConfigRequest = {
  rewardType: 'VOTE',
  amount: 1,
  probability: 100,
  dailyLimit: null,
  requiredVotes: null,
  threshold: null,
  isFixed: false,
  enabled: true,
};

export const RewardConfigPanel = () => {
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<RewardConfigRequest>(initialForm);
  const [showHistory, setShowHistory] = useState(false);
  const [historyPage, setHistoryPage] = useState(0);
  const [activeCategory, setActiveCategory] = useState<CategoryTab>('투표');

  const queryClient = useQueryClient();

  /** APIs */
  const { data: configs, isLoading } = useQuery({
    queryKey: ['adminRewardConfigs'],
    queryFn: () => rewardApi.getConfigs().then(res => res.data.results),
  });

  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['adminRewardConfigHistory', historyPage],
    queryFn: () => rewardApi.getConfigHistory(historyPage, 10).then(res => res.data.results),
    enabled: showHistory,
  });

  const createMutation = useMutation({
    mutationFn: (params: RewardConfigRequest) => rewardApi.createConfig(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminRewardConfigs'] });
      toast.success('설정이 추가되었습니다.');
      closeForm();
    },
    onError: () => toast.error('설정 추가에 실패했습니다.'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, params }: { id: number; params: RewardConfigRequest }) =>
      rewardApi.updateConfig(id, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminRewardConfigs'] });
      toast.success('설정이 수정되었습니다.');
      closeForm();
    },
    onError: () => toast.error('설정 수정에 실패했습니다.'),
  });

  /** 토글 전용 mutation — 폼과 무관하게 동작 */
  const toggleMutation = useMutation({
    mutationFn: ({ id, params }: { id: number; params: RewardConfigRequest }) =>
      rewardApi.updateConfig(id, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminRewardConfigs'] });
      toast.success('상태가 변경되었습니다.');
    },
    onError: () => toast.error('상태 변경에 실패했습니다.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => rewardApi.deleteConfig(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminRewardConfigs'] });
      toast.success('설정이 삭제되었습니다.');
    },
    onError: () => toast.error('설정 삭제에 실패했습니다.'),
  });

  /** Handlers */
  const openCreateForm = () => {
    setEditingId(null);
    setForm({ ...initialForm, rewardType: CATEGORY_TYPES[activeCategory][0] });
    setIsCreateFormOpen(true);
  };

  const openEditForm = (config: RewardConfigItem) => {
    setIsCreateFormOpen(false);
    setEditingId(config.id);
    setForm({
      rewardType: config.rewardType,
      amount: config.amount,
      probability: config.probability,
      dailyLimit: config.dailyLimit,
      requiredVotes: config.requiredVotes,
      threshold: config.threshold,
      isFixed: config.isFixed,
      enabled: config.enabled,
    });
  };

  const closeForm = () => {
    setIsCreateFormOpen(false);
    setEditingId(null);
    setForm(initialForm);
  };

  const handleSubmit = () => {
    if (editingId !== null) {
      updateMutation.mutate({ id: editingId, params: form });
    } else {
      createMutation.mutate(form);
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm('이 설정을 삭제하시겠습니까?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleToggleEnabled = (config: RewardConfigItem) => {
    toggleMutation.mutate({
      id: config.id,
      params: {
        rewardType: config.rewardType,
        amount: config.amount,
        probability: config.probability,
        dailyLimit: config.dailyLimit,
        requiredVotes: config.requiredVotes,
        threshold: config.threshold,
        isFixed: config.isFixed,
        enabled: !config.enabled,
      },
    });
  };

  /** 현재 탭의 리워드 타입만 필터 + 종류별 그룹핑 */
  const configList = Array.isArray(configs) ? configs : [];
  const activeCategoryTypes = CATEGORY_TYPES[activeCategory];
  const filteredConfigs = configList.filter(c => activeCategoryTypes.includes(c.rewardType));
  const groupedConfigs = filteredConfigs.reduce<Record<string, RewardConfigItem[]>>((acc, config) => {
    const key = config.rewardType;
    if (!acc[key]) acc[key] = [];
    acc[key].push(config);
    return acc;
  }, {});

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  /** 공통 폼 필드 렌더 */
  const renderFormFields = () => (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      <div>
        <label className="text-xs text-gray-500 block mb-1">리워드 종류</label>
        <select
          className="border rounded-md px-3 py-1.5 text-sm w-full"
          value={form.rewardType}
          onChange={e => setForm({ ...form, rewardType: e.target.value as RewardType })}
          disabled={editingId !== null}
        >
          {activeCategoryTypes.map(type => (
            <option key={type} value={type}>{REWARD_TYPE_LABELS[type]}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-1">금액 (원)</label>
        <input
          type="number"
          className="border rounded-md px-3 py-1.5 text-sm w-full"
          value={form.amount}
          onChange={e => setForm({ ...form, amount: Number(e.target.value) })}
          min={1}
        />
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-1">확률 (%)</label>
        <input
          type="number"
          className="border rounded-md px-3 py-1.5 text-sm w-full"
          value={form.probability}
          onChange={e => setForm({ ...form, probability: Number(e.target.value) })}
          min={0}
          max={100}
        />
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-1">일일제한</label>
        <input
          type="number"
          className="border rounded-md px-3 py-1.5 text-sm w-full"
          value={form.dailyLimit ?? ''}
          onChange={e => setForm({ ...form, dailyLimit: e.target.value ? Number(e.target.value) : null })}
          placeholder="-"
        />
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-1">필요투표</label>
        <input
          type="number"
          className="border rounded-md px-3 py-1.5 text-sm w-full"
          value={form.requiredVotes ?? ''}
          onChange={e => setForm({ ...form, requiredVotes: e.target.value ? Number(e.target.value) : null })}
          placeholder="-"
        />
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-1">임계값</label>
        <input
          type="number"
          className="border rounded-md px-3 py-1.5 text-sm w-full"
          value={form.threshold ?? ''}
          onChange={e => setForm({ ...form, threshold: e.target.value ? Number(e.target.value) : null })}
          placeholder="-"
        />
      </div>
      <div className="flex items-end pb-1">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={form.isFixed ?? false}
            onChange={e => setForm({ ...form, isFixed: e.target.checked })}
            className="rounded"
          />
          고정 금액
        </label>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">리워드 설정</h3>
        <Button size="sm" onClick={openCreateForm} className="flex items-center gap-1">
          <Plus className="w-3 h-3" />
          추가
        </Button>
      </div>

      {/* 카테고리 탭 */}
      <div className="flex border-b">
        {CATEGORY_TABS.map(tab => (
          <button
            key={tab}
            onClick={() => {
              setActiveCategory(tab);
              closeForm();
            }}
            className={`px-4 py-2 text-sm font-medium transition-colors relative ${
              activeCategory === tab
                ? 'text-[#030213]'
                : 'text-[#6B7280] hover:text-[#030213]'
            }`}
          >
            {tab}
            {activeCategory === tab && (
              <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#030213]" />
            )}
          </button>
        ))}
      </div>

      {/* 추가 폼 — 헤더 바로 아래에만 표시 */}
      {isCreateFormOpen && (
        <Card className="border-blue-200 bg-blue-50/30">
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold">설정 추가</h4>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            {renderFormFields()}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={closeForm}>취소</Button>
              <Button size="sm" onClick={handleSubmit} disabled={createMutation.isPending}>
                추가
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* 설정 테이블 (종류별 그룹) */}
      {isLoading ? (
        <div className="text-center py-8 text-gray-400">로딩 중...</div>
      ) : Object.keys(groupedConfigs).length === 0 ? (
        <div className="text-center py-8 text-gray-400">등록된 설정이 없습니다.</div>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedConfigs).map(([type, items]) => {
            const editingItem = editingId !== null ? items.find(i => i.id === editingId) : null;
            const isEditingThisGroup = !!editingItem;

            return (
              <Card key={type}>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {REWARD_TYPE_LABELS[type] ?? type}
                      </Badge>
                      <span className="text-xs text-gray-400">{items.length}개 설정</span>
                    </div>
                    {/* 프로모션 성격 리워드: 카드 헤더에 ON/OFF 토글 배치.
                        🔴 M-024 — 여기 없으면 운영에서 켜고 끌 방법이 SQL 뿐이다. */}
                    {TOGGLEABLE_TYPES.includes(type as RewardType) && items[0] && (
                      <button
                        onClick={() => handleToggleEnabled(items[0])}
                        disabled={toggleMutation.isPending}
                        className={`text-xs px-3 py-1 rounded-full font-medium transition-colors ${
                          items[0].enabled
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        {items[0].enabled ? '지급중' : '중단'}
                      </button>
                    )}
                  </div>

                  {/* 데스크톱 */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-gray-500">
                          <th className="pb-2 pr-3">금액</th>
                          <th className="pb-2 pr-3">확률(%)</th>
                          <th className="pb-2 pr-3">일일제한</th>
                          <th className="pb-2 pr-3">필요투표</th>
                          <th className="pb-2 pr-3">임계값</th>
                          <th className="pb-2 pr-3">고정</th>
                          <th className="pb-2 text-right">작업</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map(config => (
                          <tr key={config.id} className="border-b last:border-0 hover:bg-gray-50">
                            <td className="py-2 pr-3 font-medium">{config.amount.toLocaleString()}원</td>
                            <td className="py-2 pr-3">{config.probability}%</td>
                            <td className="py-2 pr-3 text-gray-400">{config.dailyLimit ?? '-'}</td>
                            <td className="py-2 pr-3 text-gray-400">{config.requiredVotes ?? '-'}</td>
                            <td className="py-2 pr-3 text-gray-400">{config.threshold ?? '-'}</td>
                            <td className="py-2 pr-3">
                              {config.isFixed
                                ? <span className="text-green-600 text-xs">O</span>
                                : <span className="text-gray-300 text-xs">-</span>
                              }
                            </td>
                            <td className="py-2 text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditForm(config)}
                                  className="h-7 w-7 p-0"
                                >
                                  <Pencil className="w-3 h-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(config.id)}
                                  className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* 모바일 */}
                  <div className="md:hidden space-y-2">
                    {items.map(config => (
                      <div key={config.id} className="border rounded-lg p-3 space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{config.amount.toLocaleString()}원</span>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEditForm(config)} className="h-7 w-7 p-0">
                              <Pencil className="w-3 h-3" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(config.id)} className="h-7 w-7 p-0 text-red-500">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        <div className="text-xs text-gray-500">
                          확률 {config.probability}%
                          {config.dailyLimit != null && ` · 일일제한 ${config.dailyLimit}`}
                          {config.requiredVotes != null && ` · 필요투표 ${config.requiredVotes}`}
                          {config.threshold != null && ` · 임계값 ${config.threshold}`}
                          {config.isFixed && ' · 고정'}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* 수정 폼 — 해당 카드 내부에 인라인으로 표시 */}
                  {isEditingThisGroup && (
                    <div className="mt-4 pt-4 border-t border-blue-100 bg-blue-50/30 rounded-lg p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-blue-700">
                          {REWARD_TYPE_LABELS[type] ?? type} 수정
                        </h4>
                        <button onClick={closeForm} className="text-gray-400 hover:text-gray-600">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      {renderFormFields()}
                      <div className="flex gap-2 justify-end">
                        <Button variant="outline" size="sm" onClick={closeForm}>취소</Button>
                        <Button
                          size="sm"
                          onClick={handleSubmit}
                          disabled={updateMutation.isPending}
                        >
                          수정
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* 변경 이력 (접기/펼치기) */}
      <div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowHistory(!showHistory)}
          className="flex items-center gap-1 text-gray-500"
        >
          {showHistory ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          설정 변경 이력
        </Button>

        {showHistory && (
          <Card className="mt-2">
            <div className="p-6">
              {historyLoading ? (
                <div className="text-center py-4 text-gray-400">로딩 중...</div>
              ) : !historyData?.content?.length ? (
                <div className="text-center py-4 text-gray-400">변경 이력이 없습니다.</div>
              ) : (
                <>
                  <div className="space-y-3">
                    {historyData.content.map((item: RewardConfigHistoryItem) => (
                      <div key={item.id} className="border rounded-lg p-3 text-sm space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{item.changedBy}</span>
                          <span className="text-xs text-gray-400">{formatDate(item.changedAt)}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-gray-500">변경 전:</span>
                            <pre className="mt-1 bg-red-50 p-2 rounded text-xs overflow-x-auto whitespace-pre-wrap">
                              {item.beforeConfig}
                            </pre>
                          </div>
                          <div>
                            <span className="text-gray-500">변경 후:</span>
                            <pre className="mt-1 bg-green-50 p-2 rounded text-xs overflow-x-auto whitespace-pre-wrap">
                              {item.afterConfig}
                            </pre>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* 페이지네이션 */}
                  <div className="flex items-center justify-between mt-4 pt-3 border-t">
                    <span className="text-xs text-gray-500">
                      총 {historyData.totalElements}건
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={historyPage === 0}
                        onClick={() => setHistoryPage(p => p - 1)}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm text-gray-600">
                        {historyPage + 1} / {historyData.totalPages || 1}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={historyPage >= (historyData.totalPages || 1) - 1}
                        onClick={() => setHistoryPage(p => p + 1)}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};
